import { useCallback, useEffect, useMemo, useState } from 'react';

import { Form, Input, notification, Select, TreeSelect } from 'antd';

import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import {
  DOMAIN_TYPE_OPTIONS,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  FORM_RULES,
  MODAL_WIDTHS,
  PLACEHOLDER_MESSAGES,
  TREE_DEPTH,
  VALIDATION_MESSAGES,
  VALIDATION_RULES,
} from '@/shared/lib/constants';
import { calculateFqnDepth, getParentFqn } from '@/shared/lib/format/fqn';
import { areArraysEqual, transformOwnersToSelectOptions } from '@/shared/lib/utils';
import type { JsonPatchOperation } from '@/shared/types/common';
import { BaseModal } from '@/shared/ui/BaseModal';
import { Chip } from '@/shared/ui/Chip';
import { DescriptionTextArea } from '@/shared/ui/DescriptionTextArea';
import { Icon } from '@/shared/ui/Icon';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import type { DomainItem } from '@/entities/domain';
import { useUserOwners } from '@/entities/owner';
import { convertTagsToTree, useAvailableTags } from '@/entities/tags';

import type { CreateDomainRequest, DomainType } from '@/features/domain/api';
import { useCreateDomain, useUpdateDomain } from '@/features/domain/model';

import styles from './DomainFormModal.module.css';

type FormValues = {
  name: string;
  displayName?: string;
  description: string;
  domainType: DomainType;
  tags?: string[];
  owners?: string[];
  experts?: string[];
};

type ParentInfo = {
  parentFqn?: string;
  parentName?: string;
};

type DomainFormModalProps = {
  open: boolean;
  mode?: 'create' | 'edit';
  parentInfo?: ParentInfo;
  domainData?: DomainItem;
  domainFqn?: string;
  onClose: () => void;
  onSuccess?: (parentFqn: string) => void;
};

const DEPTH_MESSAGES = {
  MAX_DEPTH_ERROR: '최대 계층에 도달하여 더 이상 하위 도메인을 생성할 수 없습니다',
} as const;

export const DomainFormModal = ({
  open,
  mode = 'create',
  parentInfo,
  domainData,
  domainFqn,
  onClose,
  onSuccess,
}: DomainFormModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [tagSearchValue, setTagSearchValue] = useState('');
  const createMutation = useCreateDomain();
  const updateMutation = useUpdateDomain(domainFqn);
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const { validateLength: validateTagSearch } = useSearchLengthValidation({
    maxLength: 30,
    warningMessage: VALIDATION_MESSAGES.TAG_SEARCH_MAX_LENGTH,
    debounceMs: 1000,
    shouldTruncate: true, // TreeSelect는 maxLength prop이 동작하지 않으므로 수동 truncate
  });

  const { data: usersData } = useUserOwners();
  const { data: availableTags = [] } = useAvailableTags();

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const isEditMode = mode === 'edit';

  const currentParentDepth = useMemo(() => {
    if (isEditMode || !parentInfo?.parentFqn) return 0;
    return calculateFqnDepth(parentInfo.parentFqn);
  }, [isEditMode, parentInfo?.parentFqn]);

  const newDomainDepth = currentParentDepth + 1;
  const cannotCreate = newDomainDepth > TREE_DEPTH.DOMAIN;

  const modalTitle = useMemo(() => {
    if (isEditMode) {
      return `도메인 수정 (${domainData?.displayName || domainData?.name || ''})`;
    }
    if (parentInfo?.parentName) {
      return `하위 도메인 추가 (${parentInfo.parentName})`;
    }
    return '도메인 추가';
  }, [isEditMode, domainData, parentInfo]);

  const tagsTreeData = useMemo(() => convertTagsToTree(availableTags), [availableTags]);

  const userOptions = useMemo(() => transformOwnersToSelectOptions(usersData), [usersData]);

  useEffect(() => {
    if (open) {
      if (isEditMode && domainData) {
        form.setFieldsValue({
          name: domainData.name,
          displayName: domainData.displayName || '',
          description: domainData.description || '',
          domainType: domainData.domainType as DomainType,
          tags: domainData.tags?.map((t) => t.tagFQN) || [],
          owners: domainData.owners?.map((o) => o.id) || [],
          experts: domainData.experts?.map((e) => e.id) || [],
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, form, isEditMode, domainData]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const generatePatchOperations = useCallback(
    (values: FormValues): JsonPatchOperation[] => {
      if (!domainData) return [];

      const operations: JsonPatchOperation[] = [];

      if (values.displayName !== (domainData.displayName || '')) {
        if (values.displayName) {
          operations.push({
            op: domainData.displayName ? 'replace' : 'add',
            path: '/displayName',
            value: values.displayName,
          });
        } else if (domainData.displayName) {
          operations.push({ op: 'remove', path: '/displayName' });
        }
      }

      if (values.description !== (domainData.description || '')) {
        if (values.description) {
          operations.push({
            op: domainData.description ? 'replace' : 'add',
            path: '/description',
            value: values.description,
          });
        }
      }

      const currentTags = domainData.tags?.map((t) => t.tagFQN) || [];
      const newTags = values.tags || [];
      if (!areArraysEqual(currentTags, newTags)) {
        const tagValues = newTags.map((tagFQN) => {
          const tagInfo = availableTags.find((t) => t.value === tagFQN);
          const tagName = tagFQN.split('.').pop() || tagFQN;
          return {
            tagFQN,
            source: 'Classification',
            name: tagName,
            displayName: tagInfo?.label || tagName,
            description: tagInfo?.description,
          };
        });
        operations.push({
          op: currentTags.length > 0 ? 'replace' : 'add',
          path: '/tags',
          value: tagValues,
        });
      }

      const currentOwners = domainData.owners?.map((o) => o.id) || [];
      const newOwners = values.owners || [];
      if (!areArraysEqual(currentOwners, newOwners)) {
        const ownerValues = newOwners.map((id) => ({ id, type: 'user' }));
        operations.push({
          op: currentOwners.length > 0 ? 'replace' : 'add',
          path: '/owners',
          value: ownerValues,
        });
      }

      const currentExperts = domainData.experts?.map((e) => e.id) || [];
      const newExperts = values.experts || [];
      if (!areArraysEqual(currentExperts, newExperts)) {
        const expertValues = newExperts.map((id) => ({ id, type: 'user' }));
        operations.push({
          op: currentExperts.length > 0 ? 'replace' : 'add',
          path: '/experts',
          value: expertValues,
        });
      }

      return operations;
    },
    [domainData, availableTags]
  );

  const transformFormValuesToRequest = useCallback(
    (values: FormValues): CreateDomainRequest => {
      const owners = (values.owners || []).map((id) => ({
        id,
        type: 'user',
      }));

      const tags = (values.tags || []).map((tagFQN) => {
        const tagInfo = availableTags.find((t) => t.value === tagFQN);
        const tagName = tagFQN.split('.').pop() || tagFQN;
        return {
          tagFQN,
          source: 'Classification' as const,
          name: tagName,
          displayName: tagInfo?.label || tagName,
          description: tagInfo?.description,
          labelType: 'Manual' as const,
          state: 'Confirmed' as const,
        };
      });

      const request: CreateDomainRequest = {
        domainType: values.domainType,
        name: values.name,
        displayName: values.displayName || undefined,
        description: values.description || undefined,
        owners,
        experts: values.experts || [],
        tags,
      };

      if (parentInfo?.parentFqn) {
        request.parent = parentInfo.parentFqn;
      }

      return request;
    },
    [availableTags, parentInfo]
  );

  const executeCreate = useCallback(
    async (values: FormValues) => {
      const request = transformFormValuesToRequest(values);
      await createMutation.mutateAsync(request);
      handleClose();

      const refreshFqn = parentInfo?.parentFqn || '';
      onSuccess?.(refreshFqn);
    },
    [transformFormValuesToRequest, createMutation, handleClose, parentInfo?.parentFqn, onSuccess]
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        if (isEditMode && domainData) {
          const operations = generatePatchOperations(values);
          if (operations.length === 0) {
            notification.info({
              message: '변경 사항 없음',
              description: '수정된 내용이 없습니다.',
              placement: 'bottomLeft',
              duration: 3,
            });
            handleClose();
            return;
          }
          await updateMutation.mutateAsync({
            domainId: domainData.id,
            operations,
          });
          handleClose();

          if (domainFqn) {
            onSuccess?.(getParentFqn(domainFqn));
          }
          return;
        }

        if (cannotCreate) {
          notification.error({
            message: '도메인 생성 불가',
            description: DEPTH_MESSAGES.MAX_DEPTH_ERROR,
            placement: 'bottomLeft',
            duration: 5,
          });
          return;
        }

        await executeCreate(values);
      } catch (error) {
        console.error('Failed to submit domain form:', error);
      }
    },
    [
      isEditMode,
      domainData,
      generatePatchOperations,
      updateMutation,
      domainFqn,
      handleClose,
      onSuccess,
      cannotCreate,
      executeCreate,
    ]
  );

  return (
    <BaseModal
      title={modalTitle}
      open={open}
      onClose={handleClose}
      width={MODAL_WIDTHS.STANDARD_FORM}
      onOk={() => form.submit()}
      okText={isEditMode ? '저장' : '생성'}
      confirmLoading={isLoading}>
      <Form form={form} layout="vertical" onFinish={handleSubmit} className={styles.form}>
        <Form.Item
          name="name"
          label={FORM_LABELS.NAME}
          rules={[...FORM_RULES.REQUIRED_NAME, ...FORM_RULES.DOMAIN_NAME]}
          validateFirst>
          <Input placeholder={FORM_PLACEHOLDERS.NAME} disabled={isEditMode} allowClear />
        </Form.Item>

        <Form.Item name="displayName" label={FORM_LABELS.DISPLAY_NAME}>
          <Input placeholder={FORM_PLACEHOLDERS.DISPLAY_NAME} allowClear />
        </Form.Item>

        <Form.Item
          name="description"
          label={FORM_LABELS.DESCRIPTION}
          rules={[...FORM_RULES.REQUIRED_DESCRIPTION, ...FORM_RULES.DOMAIN_DESCRIPTION]}
          validateTrigger={['onSubmit']}
          validateFirst>
          <DescriptionTextArea
            placeholder={FORM_PLACEHOLDERS.DESCRIPTION}
            showHelper
            maxLength={VALIDATION_RULES.DOMAIN_DESCRIPTION_MAX_LENGTH}
          />
        </Form.Item>

        <Form.Item name="domainType" label={FORM_LABELS.DOMAIN_TYPE} rules={FORM_RULES.REQUIRED_DOMAIN_TYPE}>
          <Select placeholder={FORM_PLACEHOLDERS.DOMAIN_TYPE} options={DOMAIN_TYPE_OPTIONS} disabled={isEditMode} />
        </Form.Item>

        <Form.Item name="tags" label={FORM_LABELS.TAGS}>
          <TreeSelect
            multiple
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_CHILD}
            placeholder={PLACEHOLDER_MESSAGES.TAG_SELECT}
            allowClear
            treeData={tagsTreeData}
            treeDefaultExpandAll
            treeLine
            tagRender={(props) => {
              const { label, onClose } = props;
              return <Chip icon={<Icon name="tag" size={12} />} label={label as string} onClose={onClose} />;
            }}
            maxTagCount="responsive"
            searchValue={tagSearchValue}
            onSearch={(value) => {
              const validatedValue = validateTagSearch(value);
              setTagSearchValue(validatedValue);
            }}
          />
        </Form.Item>

        {showOwners && (
          <Form.Item name="owners" label={FORM_LABELS.OWNERS}>
            <Select mode="multiple" placeholder={PLACEHOLDER_MESSAGES.OWNER_SELECT} allowClear options={userOptions} />
          </Form.Item>
        )}

        {showOwners && (
          <Form.Item name="experts" label={FORM_LABELS.EXPERTS}>
            <Select mode="multiple" placeholder={PLACEHOLDER_MESSAGES.OWNER_SELECT} allowClear options={userOptions} />
          </Form.Item>
        )}
      </Form>
    </BaseModal>
  );
};
